import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import LandingAtmosphere from '../components/ui/LandingAtmosphere';
import ParrotEmblem from '../components/ui/ParrotEmblem';
import { useAuth } from '../contexts/AuthContext';
import { authApi, type AuthUser } from '../api/auth';

function emailFromSetPasswordToken(token: string): string {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return '';
    const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { email?: string };
    return typeof payload.email === 'string' ? payload.email : '';
  } catch {
    return '';
  }
}

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const emailHint = useMemo(() => emailFromSetPasswordToken(token), [token]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (!token) setError('This set-password link is missing a token. Use the link from your welcome email.');
  }, [token]);

  const completeAuth = (jwt: string, user: AuthUser) => {
    login(jwt, {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    });
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      const result = await authApi.setPassword(token, password);
      completeAuth(result.token, result.user);
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || 'Could not set password';
      setError(
        /timeout|abort|fetch|network/i.test(msg)
          ? 'Your instance is starting (it sleeps when idle). Wait a few seconds and try again.'
          : msg,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="marketing-landing relative min-h-screen">
      <LandingAtmosphere className="fixed inset-0" />
      <main className="relative z-10 mx-auto max-w-lg px-4 py-16 sm:px-6">
        <div className="mb-6 flex items-center gap-2.5">
          <ParrotEmblem className="h-9 w-9" animated={false} />
          <span className="marketing-brand-title text-2xl font-bold">BirdMan</span>
        </div>
        <div className="rounded-3xl border border-teal-900/10 bg-white/85 p-7 backdrop-blur-xl">
          <h1 className="marketing-display text-2xl font-semibold text-[var(--bm-landing-ink)]">
            Set your password
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            This finishes setup for your dedicated instance. You will be signed in afterwards.
          </p>
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <label className="sr-only">
              Email
              <input
                type="email"
                name="username"
                autoComplete="username"
                value={emailHint}
                readOnly
                tabIndex={-1}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              New password
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Confirm password
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={6}
                required
              />
            </label>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button type="submit" className="btn btn-primary w-full" disabled={isLoading || !token}>
            {isLoading ? 'Starting your instance…' : 'Save password and sign in'}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-slate-500">
            Already set a password?{' '}
            <Link to="/login" className="font-medium text-teal-800 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
